import React from 'react';
import axios from 'axios';

import { useNavigate } from 'react-router-dom';
import { Loader, Button } from 'rsuite';
import { MdLocationOn, MdDateRange, MdAccessTime, MdExpandMore, MdReceipt, MdMessage } from 'react-icons/md';

import { checkIfLoggedIn, formatPrice, formatDateRange, formatDate } from '../services/utilService';
import CampLayout from './common/CampLayout'

const apiUrl = process.env.REACT_APP_SERVER_API_URL;

// Note: no payment-status chip here on purpose. Payments are settled outside
// the platform (pay-later payments and refunds happen off-platform), so any
// status shown after checkout could be stale or wrong.

const Orders = () => {
    const navigate = useNavigate();

    const [orders, setOrders] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [expandedOrders, setExpandedOrders] = React.useState(new Set());

    React.useEffect(() => {
        checkIfLoggedIn();

        if (!localStorage.getItem("user-id")) {
            navigate("/login");
        } else {
            let mounted = true;
            setIsLoading(true);
            axios.get(`${apiUrl}/users/${localStorage.getItem('user-id')}/orders`)
                .then((response) => {
                    if (mounted) {
                        setOrders(response.data
                            .sort((a, b) => a.createdDate < b.createdDate ? 1 : -1)
                        );
                    }
                })
                .finally(() => {
                    if (mounted) {
                        setIsLoading(false);
                    }
                });
            return () => mounted = false;
        }
    }, [navigate]);

    const toggleOrderExpansion = (orderId) => {
        const newExpanded = new Set(expandedOrders);
        if (newExpanded.has(orderId)) {
            newExpanded.delete(orderId);
        } else {
            newExpanded.add(orderId);
        }
        setExpandedOrders(newExpanded);
    };

    return (
        <CampLayout page="orders">
            <div className="px-7 pb-7">
                <h1 className="page-title mb-6">My Orders</h1>
                
                {isLoading ? (
                    <div className="flex justify-center items-center p-8">
                        <Loader size="lg" content="Loading orders..." />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                            <MdReceipt size={40} className="mx-auto text-gray-300 mb-3" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">No orders yet</h3>
                            <p className="text-gray-600 mb-4">
                                Start exploring our amazing camps and create your first order.
                            </p>
                            <Button appearance="primary" onClick={() => navigate('/book')}>
                                Book a camp
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {orders.map((order) => {
                            const isExpanded = expandedOrders.has(order._id);
                            
                            return (
                                <div key={order._id}>
                                    {/* Order Header - Clickable */}
                                    <div 
                                        className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                                        onClick={() => toggleOrderExpansion(order._id)}
                                    >
                                        <div className="flex items-center">
                                            <div className="mr-3">
                                                <MdExpandMore
                                                    size={28}
                                                    className={`text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h2 className="text-xl font-semibold text-gray-900 m-0">
                                                            #{order.orderNumber}
                                                        </h2>
                                                        <p className="text-sm text-gray-600 sm:hidden">
                                                            Submitted on {formatDate(order.createdDate)}
                                                        </p>
                                                        <p className="text-sm text-gray-600 hidden sm:block">
                                                            Submitted on {formatDate(order.createdDate)} • {order.items.length} booking{order.items.length !== 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                    <div className="text-right ml-4 sm:hidden">
                                                        <div className="text-lg font-bold text-gray-800">
                                                            ${formatPrice(order.paidAmount || order.items.reduce((total, item) => {
                                                                const price = typeof item.price === 'string'
                                                                    ? item.price.replace(/[$,]/g, '')
                                                                    : item.price || 0;
                                                                return total + Number(price);
                                                            }, 0) - (order.discountAmount || 0))}<span className="currency-suffix">SGD</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="text-right mr-4 hidden sm:block">
                                                <div className="text-lg font-bold text-gray-800">
                                                    ${formatPrice(order.paidAmount || order.items.reduce((total, item) => {
                                                        const price = typeof item.price === 'string'
                                                            ? item.price.replace(/[$,]/g, '')
                                                            : item.price || 0;
                                                        return total + Number(price);
                                                    }, 0) - (order.discountAmount || 0))}<span className="currency-suffix">SGD</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Collapsible Content */}
                                    {isExpanded && (
                                        <>
                                            {/* Desktop Layout */}
                                    <div className="hidden lg:block">
                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                            <div className="grid gap-4 p-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                                                {order.items.sort((a, b) => new Date(a.week.startDate) - new Date(b.week.startDate)).map(item => (
                                                    <div key={item.week._id + item.kid._id} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                                                        {/* Child Name and Camp */}
                                                        <div className="mb-3">
                                                            <h3 className="font-semibold text-lg text-gray-900">
                                                                {item.kid.firstName} {item.kid.lastName}
                                                            </h3>
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                {item.week.theme}
                                                            </p>
                                                        </div>

                                                        {/* Location */}
                                                        <div className="flex items-start mb-2">
                                                            <MdLocationOn className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                                                            <div className="text-sm">
                                                                <span className="font-medium text-gray-900">{item.week.location.name}</span>
                                                                {item.week.location.address && (
                                                                    <span className="text-gray-500 block">{item.week.location.address}</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Dates and Day Option */}
                                                        <div className="flex items-center mb-2">
                                                            <MdDateRange className="text-gray-400 mr-2 flex-shrink-0" size={16} />
                                                            <span className="text-sm text-gray-700">
                                                                {formatDateRange(item.week.startDate, item.week.endDate)}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center mb-2">
                                                            <MdAccessTime className="text-gray-400 mr-2 flex-shrink-0" size={16} />
                                                            <span className="text-sm text-gray-700">{item.dayOption}</span>
                                                        </div>

                                                        {/* Transport */}
                                                        <div className="text-sm text-gray-700">
                                                            <span className="text-gray-500">Transport:</span> {item.busOption || 'None'}
                                                        </div>

                                                        {/* Price */}
                                                        {item.price && (
                                                            <div className="mt-3 pt-3 border-t border-gray-200 text-right">
                                                                <span className="text-lg font-semibold text-gray-900">${formatPrice(item.price)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Order Total Breakdown - Desktop */}
                                            {(() => {
                                                const seasonDiscountedSubtotal = order.originalAmount ||
                                                    order.items.reduce((total, item) => {
                                                        const price = typeof item.price === 'string'
                                                            ? item.price.replace(/[$,]/g, '')
                                                            : item.price || 0;
                                                        return total + Number(price);
                                                    }, 0);
                                                const fullSubtotal = order.items.reduce((total, item) => {
                                                    const sp = Number(item?.week?.season?.price);
                                                    if (!isNaN(sp) && sp > 0) return total + sp;
                                                    const price = typeof item.price === 'string'
                                                        ? Number(item.price.replace(/[$,]/g, ''))
                                                        : Number(item.price) || 0;
                                                    return total + price;
                                                }, 0);
                                                const seasonSaving = Math.max(0, fullSubtotal - seasonDiscountedSubtotal);
                                                const promoSaving = order.discountAmount || 0;
                                                const total = order.paidAmount != null
                                                    ? order.paidAmount
                                                    : (seasonDiscountedSubtotal - promoSaving);
                                                if (seasonSaving === 0 && promoSaving === 0) return null;
                                                return (
                                                    <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                                                        <div className="max-w-sm ml-auto space-y-1.5">
                                                            <div className="flex justify-between text-sm">
                                                                <span className="text-gray-700">Subtotal</span>
                                                                <span className="text-gray-800">${formatPrice(fullSubtotal)}</span>
                                                            </div>
                                                            {seasonSaving > 0 && (
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-green-700">Standard discount</span>
                                                                    <span className="text-green-700">-${formatPrice(seasonSaving)}</span>
                                                                </div>
                                                            )}
                                                            {promoSaving > 0 && (
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-green-700">
                                                                        Promo code{order.promoCodeString ? ` (${order.promoCodeString})` : ''}
                                                                    </span>
                                                                    <span className="text-green-700">-${formatPrice(promoSaving)}</span>
                                                                </div>
                                                            )}
                                                            <div className="flex justify-between border-t pt-2 mt-1">
                                                                <span className="text-gray-800 font-semibold">Total</span>
                                                                <span className="text-gray-900 font-bold">${formatPrice(total)}<span className="currency-suffix">SGD</span></span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Special Requests - Desktop */}
                                            {order.comments && (
                                                <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                                                    <div className="flex items-start">
                                                        <MdMessage className="text-gray-400 mr-3 mt-0.5 flex-shrink-0" size={18} />
                                                        <div>
                                                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Special Requests</h4>
                                                            <p className="text-sm text-gray-700 leading-relaxed">{order.comments}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>

                                    {/* Mobile Layout */}
                                    <div className="space-y-4 block lg:hidden">
                                        {order.items.sort((a, b) => new Date(a.week.startDate) - new Date(b.week.startDate)).map(item => (
                                            <div key={item.week._id + item.kid._id} className="bg-white rounded-lg border border-gray-200 p-4">
                                                {/* Header with Child Name */}
                                                <div className="mb-3">
                                                    <h3 className="font-semibold text-lg text-gray-900">
                                                        {item.kid.firstName} {item.kid.lastName}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {item.week.theme}
                                                    </p>
                                                </div>

                                                {/* Details */}
                                                <div className="space-y-3">
                                                    {/* Location */}
                                                    <div className="flex items-start text-sm">
                                                        <MdLocationOn className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                                                        <div className="flex-1">
                                                            <span className="font-medium text-gray-900">{item.week.location.name}</span>
                                                            {item.week.location.address && (
                                                                <span className="text-gray-500 ml-1">• {item.week.location.address}</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Dates and Day Option */}
                                                    <div className="flex items-center text-sm">
                                                        <MdDateRange className="text-gray-400 mr-2 flex-shrink-0" size={16} />
                                                        <span className="text-gray-700">
                                                            {formatDateRange(item.week.startDate, item.week.endDate)}
                                                        </span>
                                                        <span className="text-gray-500 ml-2">• {item.dayOption}</span>
                                                    </div>

                                                    {/* Transport */}
                                                    <div className="text-sm text-gray-700">
                                                        <span className="text-gray-500">Transport:</span> {item.busOption || 'None'}
                                                    </div>
                                                </div>

                                                {/* Price Footer */}
                                                {item.price && (
                                                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
                                                        <span className="text-sm text-gray-500">Price</span>
                                                        <span className="text-lg font-semibold text-gray-900">${formatPrice(item.price)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                    </div>
                                    
                                    {/* Order Total Breakdown - Mobile */}
                                    {(() => {
                                        const seasonDiscountedSubtotal = order.originalAmount ||
                                            order.items.reduce((total, item) => {
                                                const price = typeof item.price === 'string'
                                                    ? item.price.replace(/[$,]/g, '')
                                                    : item.price || 0;
                                                return total + Number(price);
                                            }, 0);
                                        const fullSubtotal = order.items.reduce((total, item) => {
                                            const sp = Number(item?.week?.season?.price);
                                            if (!isNaN(sp) && sp > 0) return total + sp;
                                            const price = typeof item.price === 'string'
                                                ? Number(item.price.replace(/[$,]/g, ''))
                                                : Number(item.price) || 0;
                                            return total + price;
                                        }, 0);
                                        const seasonSaving = Math.max(0, fullSubtotal - seasonDiscountedSubtotal);
                                        const promoSaving = order.discountAmount || 0;
                                        const total = order.paidAmount != null
                                            ? order.paidAmount
                                            : (seasonDiscountedSubtotal - promoSaving);
                                        if (seasonSaving === 0 && promoSaving === 0) return null;
                                        return (
                                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 lg:hidden">
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-700">Subtotal</span>
                                                        <span className="text-gray-800">${formatPrice(fullSubtotal)}</span>
                                                    </div>
                                                    {seasonSaving > 0 && (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-green-700">Standard discount</span>
                                                            <span className="text-green-700">-${formatPrice(seasonSaving)}</span>
                                                        </div>
                                                    )}
                                                    {promoSaving > 0 && (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-green-700">
                                                                Promo code{order.promoCodeString ? ` (${order.promoCodeString})` : ''}
                                                            </span>
                                                            <span className="text-green-700">-${formatPrice(promoSaving)}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between border-t pt-2 mt-1">
                                                        <span className="text-gray-800 font-semibold">Total</span>
                                                        <span className="text-gray-900 font-bold">${formatPrice(total)}<span className="currency-suffix">SGD</span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Special Requests - Mobile */}
                                    {order.comments && (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 lg:hidden">
                                            <div className="flex items-start">
                                                <MdMessage className="text-gray-400 mr-3 mt-0.5 flex-shrink-0" size={16} />
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Special Requests</h4>
                                                    <p className="text-sm text-gray-700 leading-relaxed">{order.comments}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Change-booking link */}
                                    <div className="mt-3 px-3 text-sm">
                                        <a
                                            href={`https://wa.me/6589501968?text=Hi%2C%20I%20need%20to%20change%20my%20camp%20booking.%20My%20order%20number%20is%20%23${order.orderNumber}.`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                        >
                                            Need to change this booking? Chat with us on WhatsApp
                                        </a>
                                    </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </CampLayout>
    )
}

export default Orders;
